/* TSOGalleryImages — SITE-WIDE gallery-driven imagery (the vehicle-aware
 * image layer; companion to TSOVehicle's text layer).
 *
 * Any image slot in the theme can fill itself from the customer-build
 * gallery, scored against the visitor's saved vehicle:
 *
 *   EDITOR (no code, any section's image picker):
 *     set the image ALT to  {gallery-image:landscape}
 *                           {gallery-image:square|type:frame-overlays}
 *                           {gallery-image:landscape|product:some-handle|feat}
 *     The picked image stays as the fallback (non-JS/crawlers/empty pool).
 *
 *   SECTIONS (coded):
 *     <img data-tso-gallery-image="landscape|type:exhaust-tips" ...>
 *
 * Spec grammar: orientation (landscape|square|portrait|any) then optional
 * |type:<product_type handle>  |product:<product handle>  |collection:<handle>
 * (collection: is an alias of type: — collection handles match product_type
 * handles by store convention)  |feat (require the feature_image flag).
 * Context filters are HARD (a frame-overlays slot never shows an exhaust
 * tip); the vehicle match is a SOFT ladder (generation > look-group > model
 * > make > any) so slots always fill — no vehicle = best available.
 *
 * Scoring (Ben, 2026-07-06): vehicle band (gen 4 / look-group 3 / model 2 /
 * make 1 / paint 1 — same weights as the gallery grid) + high_quality 8 +
 * feature_image 2 + recency 0-4 (30/90/180/365-day steps). HQ (8) beats max
 * recency (4) by design: an old great photo outweighs a new mediocre one.
 *
 * Data: /pages/vehicle-data?view=gallery-media-data (paginated; extended
 * 2026-07-06 with ar/lg/mk/md/pt/prods/feat/ep), cached in localStorage
 * (tso_gallery_images, 6h TTL). Fetches ONLY when the page has slots.
 * Re-picks on vehicle:selection:updated; per-page de-dupe so one photo
 * doesn't fill every slot.
 */
(function () {
  'use strict';
  if (window.TSOGalleryImages) return;

  var ENDPOINT = '/pages/vehicle-data?view=gallery-media-data';
  var CACHE_KEY = 'tso_gallery_images';
  var TTL_MS = 6 * 3600 * 1000;
  var MAX_PAGES = 20;

  function norm(s) { return String(s || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''); }
  function gid(s) { return String(s || '').replace('gid://shopify/Metaobject/', ''); }

  /* ---- scoring (exported for tests) ---- */
  function orientationOf(ar) {
    ar = Number(ar) || 1;
    if (ar >= 1.3) return 'landscape';
    if (ar <= 0.8) return 'portrait';
    return 'square';
  }

  function recencyScore(ep, nowSec) {
    var age = (nowSec - (Number(ep) || 0)) / 86400;
    if (age < 0) return 4;
    if (age <= 30) return 4;
    if (age <= 90) return 3;
    if (age <= 180) return 2;
    if (age <= 365) return 1;
    return 0;
  }

  function matchesContext(m, spec) {
    if (spec.feat && !m.feat) return false;
    if (spec.orientation && spec.orientation !== 'any' && orientationOf(m.ar) !== spec.orientation) return false;
    if (spec.type) {
      // `colls` = collection handles derived from the products in the photo
      // (2026-07-13 product-tagging migration). `pt` = the old hand-tagged
      // product_type handles, kept as a fallback until that field is retired.
      var typeHit = (m.colls || []).some(function (t) { return norm(t) === spec.type; }) ||
                    (m.pt || []).some(function (t) { return norm(t) === spec.type; });
      if (!typeHit) return false;
    }
    if (spec.product && !(m.prods || []).some(function (h) { return norm(h) === spec.product; })) return false;
    return true;
  }

  function score(m, v, nowSec) {
    var s = 0;
    if (v) {
      if (v.filter_value && gid(m.gen) === gid(v.filter_value)) s += 4;
      if (v.look_group && norm(m.lg) === norm(v.look_group)) s += 3;
      if (v.model && norm(m.md) === norm(v.model)) s += 2;
      if (v.make && norm(m.mk) === norm(v.make)) s += 1;
      if (v.paint_color) {
        var paint = norm(v.paint_color);
        if ((m.paints || []).some(function (p) { return norm(p) === paint; })) s += 1;
      }
    }
    if (m.hq) s += 8;
    if (m.feat) s += 2;
    s += recencyScore(m.ep, nowSec);
    return s;
  }

  function pick(pool, spec, v, used, nowSec) {
    nowSec = nowSec || Math.floor(Date.now() / 1000);
    var best = null, bestScore = -1;
    for (var i = 0; i < pool.length; i++) {
      var m = pool[i];
      if (used && used.indexOf(m.img) !== -1) continue;
      if (!matchesContext(m, spec)) continue;
      var sc = score(m, v, nowSec);
      if (sc > bestScore) { bestScore = sc; best = m; }
    }
    return best;
  }

  function parseSpec(str) {
    var spec = { orientation: 'any', type: null, product: null, feat: false };
    String(str || '').split('|').forEach(function (part, i) {
      part = part.trim();
      if (!part) return;
      if (i === 0) { spec.orientation = norm(part) || 'any'; return; }
      if (part === 'feat') { spec.feat = true; return; }
      var kv = part.split(':');
      var k = norm(kv[0]);
      var val = norm(kv.slice(1).join(':'));
      if (k === 'type' || k === 'collection') spec.type = val;
      else if (k === 'product') spec.product = val;
    });
    return spec;
  }

  /* ---- data ---- */
  var dataPromise = null;
  function getData() {
    if (dataPromise) return dataPromise;
    dataPromise = new Promise(function (resolve) {
      try {
        var c = JSON.parse(localStorage.getItem(CACHE_KEY));
        if (c && c.ts && Date.now() - c.ts < TTL_MS && Array.isArray(c.media)) return resolve(c.media);
      } catch (e) {}
      var all = [];
      (function page(n) {
        // NO Accept: application/json header — Shopify content-negotiates it
        // into the page RESOURCE json (ignoring ?view=) and the pool comes
        // back empty (2026-07-07 verify finding).
        fetch(ENDPOINT + '&page=' + n)
          .then(function (r) { return r.json(); })
          .then(function (d) {
            all = all.concat(d.media || []);
            if (d.has_next && n < MAX_PAGES) return page(n + 1);
            // never cache an empty pool — a transient failure would poison
            // every slot for the 6h TTL
            if (all.length) { try { localStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), media: all })); } catch (e) {} }
            resolve(all);
          })
          .catch(function () { resolve(all); });
      })(1);
    });
    return dataPromise;
  }

  function readVehicle() {
    if (window.TSOVehicle) return window.TSOVehicle.get();
    try {
      var m = document.cookie.match(/(?:^|; )vehicle_selection=([^;]*)/);
      return m ? JSON.parse(decodeURIComponent(m[1])) : null;
    } catch (e) { return null; }
  }

  function upsize(url, width) {
    return String(url).replace(/width=\d+/, 'width=' + width);
  }

  /* ---- slot applier ---- */
  var ALT_RE = /\{gallery-image:([^}]*)\}/;

  function findSlots() {
    var slots = [];
    document.querySelectorAll('img[alt*="{gallery-image"]').forEach(function (el) {
      var m = (el.getAttribute('alt') || '').match(ALT_RE);
      if (m) slots.push({ el: el, spec: parseSpec(m[1]) });
    });
    document.querySelectorAll('[data-tso-gallery-image]').forEach(function (el) {
      slots.push({ el: el, spec: parseSpec(el.getAttribute('data-tso-gallery-image')) });
    });
    return slots;
  }

  function applyAll() {
    var slots = findSlots();
    if (!slots.length) return;
    getData().then(function (pool) {
      if (!pool || !pool.length) return;
      var v = readVehicle();
      var used = [];
      slots.forEach(function (slot) {
        var el = slot.el;
        var img = el.tagName === 'IMG' ? el : el.querySelector('img');
        if (!img) return;
        if (!img.hasAttribute('data-tso-gal-fallback')) {
          img.setAttribute('data-tso-gal-fallback', img.getAttribute('src') || '');
          img.setAttribute('data-tso-gal-fallback-srcset', img.getAttribute('srcset') || '');
        }
        var m = pick(pool, slot.spec, v, used, null);
        if (!m) {
          // pool has nothing valid for this slot — restore the editor's image
          var fb = img.getAttribute('data-tso-gal-fallback');
          if (fb && img.getAttribute('src') !== fb) {
            img.setAttribute('src', fb);
            var fbs = img.getAttribute('data-tso-gal-fallback-srcset');
            if (fbs) img.setAttribute('srcset', fbs); else img.removeAttribute('srcset');
          }
          return;
        }
        used.push(m.img);
        var width = slot.spec.orientation === 'landscape' ? 2000 : 900;
        img.setAttribute('src', upsize(m.img, width));
        img.removeAttribute('srcset');
        // never leave the {gallery-image:…} spec string as the visible alt
        img.setAttribute('alt', m.veh || 'Customer build photo');
        img.setAttribute('data-tso-gal-active', '1');
      });
    });
  }

  window.TSOGalleryImages = {
    refresh: applyAll,
    // best media object for a slot spec ('landscape', 'square|type:x', ...)
    // against the saved vehicle (or an explicit override payload) — used by
    // the Klaviyo profile enrichment; triggers the dataset fetch on demand
    pickBest: function (specStr, vehicleOverride) {
      var spec = parseSpec(specStr);
      return getData().then(function (pool) {
        if (!pool || !pool.length) return null;
        return pick(pool, spec, vehicleOverride || readVehicle(), [], null);
      });
    },
    // resolves to the cached flat media array (fetch-once/paginated/localStorage,
    // same pool pickBest/applyAll use) — consumed by the builder's-guide "trucks
    // like yours" block (assets/tso-guide-builds.js) so it doesn't duplicate the
    // fetch/cache/pagination logic here
    getData: getData,
    // exported for tests + power users
    _pick: pick, _score: score, _parseSpec: parseSpec,
    _orientationOf: orientationOf, _recencyScore: recencyScore,
    _matchesContext: matchesContext
  };

  function start() {
    applyAll();
    document.addEventListener('vehicle:selection:updated', applyAll);
    document.addEventListener('shopify:section:load', applyAll); // theme editor re-renders
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
